import { useEffect, useState } from 'react'
import { RefreshCw, X } from 'lucide-react'

/**
 * Banner shown when a new Service Worker has installed in the background.
 * Listens for the 'sw:update-available' event dispatched from main.tsx and
 * gives the user a single, explicit action to reload onto the new bundle.
 *
 * We deliberately do NOT auto-reload — interrupting an in-progress sale or
 * payment screen with a forced refresh is a worse UX than a small banner.
 */
export default function UpdateBanner() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const handler = () => setShow(true)
    window.addEventListener('sw:update-available', handler)
    return () => window.removeEventListener('sw:update-available', handler)
  }, [])

  if (!show) return null

  const reload = async () => {
    if (!('serviceWorker' in navigator)) {
      window.location.reload()
      return
    }
    const reg = await navigator.serviceWorker.getRegistration()
    if (reg?.waiting) {
      // Tell the waiting SW to take control. The 'controllerchange' listener
      // in main.tsx will then reload the page once the new SW is active.
      reg.waiting.postMessage('skipWaiting')
    } else {
      window.location.reload()
    }
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[60] sm:left-auto sm:right-4 sm:max-w-sm">
      <div className="bg-primary-600 text-white rounded-xl shadow-lg p-3 flex items-center gap-3">
        <RefreshCw size={20} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Update available</p>
          <p className="text-xs opacity-80">A newer version is ready. Reload to apply.</p>
        </div>
        <button
          onClick={reload}
          className="px-3 py-1.5 bg-white text-primary-700 rounded-lg text-sm font-medium hover:bg-gray-100"
        >
          Reload
        </button>
        <button
          onClick={() => setShow(false)}
          className="p-1 hover:bg-white/10 rounded-lg shrink-0"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  )
}
